import type { Rarity } from '@kenocyber/shared';

/**
 * How long a reveal lasts, what happens when, and WHEN THE COLOUR ARRIVES.
 *
 * ## Why this is a separate, pure module
 *
 * The animation is the one part of the gacha a test cannot look at. So the
 * TIMING is pulled out of the component and made arithmetic: which phases run,
 * in which order, for how long, and at exactly what fraction the rarity stops
 * being a secret. A test asserts those integers; the component only plays them.
 *
 * Nothing here decides an OUTCOME. Every result is already persisted before the
 * first frame - this file decides how long the player waits, and not one bit of
 * what they end up owning.
 *
 * ## The secret is the whole point
 *
 * A reveal that shows red at the half-second mark has already told the player
 * they got a Mythic; everything after that is a countdown to a known number.
 * So the first 70% is deliberately COLOURLESS - cold white, silver and a
 * containment vessel that says nothing about what is inside it - and the
 * rarity is withheld until the rupture. The component reads
 * {@link colourStartsAt} rather than deciding for itself.
 *
 * There is no silhouette any more, and that was a real leak rather than a
 * stylistic choice: a ship outline is recognisable, so a player who knew the
 * roster could name the prize before the portal broke.
 */

/**
 * The phases of one reveal.
 *
 * `darken` through `tension` are the SECRET half: neutral, identical for a
 * Common and a Prismatic. `rupture` is where the colour breaks through, and
 * `transform` is the extra act only Mythic and above earn.
 */
export const REVEAL_PHASES = [
  'darken',
  'forming',
  'tension',
  'rupture',
  'transform',
  'reveal',
] as const;
export type RevealPhase = (typeof REVEAL_PHASES)[number];

/** The phases during which nothing may hint at the rarity. */
export const NEUTRAL_PHASES: readonly RevealPhase[] = Object.freeze([
  'darken',
  'forming',
  'tension',
]);

/** True while the presentation must stay colourless. */
export function isNeutralPhase(phase: RevealPhase): boolean {
  return NEUTRAL_PHASES.includes(phase);
}

/**
 * Total milliseconds per rarity.
 *
 * Slower again, and deliberately so: suspense is mostly the willingness to
 * wait, and the previous timings gave the containment vessel no time to charge
 * before it broke. The extra seconds go into forming, tension and the
 * transformation - never into a still frame.
 *
 * Thirteen seconds for a Prismatic is a long time to look at a screen, and that
 * is the trade: it happens on one pull in a thousand, and there are two skip
 * buttons for anybody who would rather not.
 */
export const REVEAL_DURATION_MS: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 5000,
  uncommon: 5400,
  rare: 5800,
  epic: 6200,
  mythic: 7600,
  legendary: 8600,
  excellent: 9400,
  perfect: 10200,
  anomaly: 11400,
  // Prismatic is longer again, and the extra seconds land mostly in the
  // suspense: the three neutral phases are a fixed SHARE of the total, so
  // raising the total lengthens the hold-your-breath stretch along with
  // everything after it.
  prismatic: 16000,
});

/** The order intensity grows in. Also the order the odds table is written in. */
export const REVEAL_RARITY_ORDER: readonly Rarity[] = Object.freeze([
  'common',
  'uncommon',
  'rare',
  'epic',
  'mythic',
  'legendary',
  'excellent',
  'perfect',
  'anomaly',
  'prismatic',
]);

/** The first rarity that earns an extra act after the rupture. */
export const FIRST_TRANSFORMING_RARITY: Rarity = 'mythic';

/** True when this rarity earns a transformation between rupture and reveal. */
export function hasTransformation(rarity: Rarity): boolean {
  return (
    REVEAL_RARITY_ORDER.indexOf(rarity) >= REVEAL_RARITY_ORDER.indexOf(FIRST_TRANSFORMING_RARITY)
  );
}

/**
 * How much of every reveal is spent hiding the rarity.
 *
 * Identical for all ten, which is what makes the secret a secret: if a
 * Prismatic charged longer than a Common, the LENGTH would give it away just
 * as surely as the colour would.
 */
export const RARITY_HIDDEN_SHARE = 0.7;

/**
 * How loud the presentation is, 0 for Common through 9 for Prismatic.
 *
 * Drives particle counts, ring counts and glow strength from ONE number. It is
 * applied only AFTER the rupture - during the neutral half every rarity draws
 * the same modest field, or the count itself would be a tell.
 */
export function revealIntensity(rarity: Rarity): number {
  return Math.max(0, REVEAL_RARITY_ORDER.indexOf(rarity));
}

/** One phase, and how long it holds. */
export interface RevealStep {
  readonly phase: RevealPhase;
  readonly durationMs: number;
}

export interface TimelineOptions {
  /**
   * `prefers-reduced-motion`. Shortens the whole thing and keeps every phase,
   * so the hiding, the rupture, the rarity and the seal all still happen - just
   * faster and without the long build.
   *
   * There is deliberately no "repeat" option any more. A ten-pull does not
   * quietly speed itself up: the player has two skip buttons if they would
   * rather not watch, and an animation that shortens itself after the first
   * result makes the first result feel like the only one that mattered.
   */
  readonly reducedMotion?: boolean;
}

/**
 * The share each phase takes, before and after the rupture.
 *
 * The three neutral phases always sum to {@link RARITY_HIDDEN_SHARE}, whether
 * or not the rarity earns a transformation - so the colour arrives at the same
 * fraction of the way through for every rarity in the game.
 */
const NEUTRAL_WEIGHTS: Readonly<Record<'darken' | 'forming' | 'tension', number>> = Object.freeze({
  darken: 0.12,
  forming: 0.3,
  tension: 0.28,
});

/** After the rupture. Two shapes: with a transformation, and without. */
const BASE_TAIL = Object.freeze({ rupture: 0.16, reveal: 0.14 });
const TRANSFORM_TAIL = Object.freeze({ rupture: 0.1, transform: 0.1, reveal: 0.1 });

/** Reduced motion keeps every phase and loses 55% of the length. */
const REDUCED_SCALE = 0.45;

/**
 * The full timeline for one reveal.
 *
 * The steps always sum to the rarity's total - the last one absorbs the
 * rounding - so a test can assert the total and the phases from the same source
 * the component plays.
 */
export function revealTimeline(
  rarity: Rarity,
  options: TimelineOptions = {},
): readonly RevealStep[] {
  const total = Math.round(
    REVEAL_DURATION_MS[rarity] * (options.reducedMotion ? REDUCED_SCALE : 1),
  );
  const tail = hasTransformation(rarity) ? TRANSFORM_TAIL : BASE_TAIL;

  const steps: RevealStep[] = [];
  let spent = 0;
  const push = (phase: RevealPhase, share: number) => {
    const durationMs = Math.round(total * share);
    steps.push({ phase, durationMs });
    spent += durationMs;
  };

  push('darken', NEUTRAL_WEIGHTS.darken);
  push('forming', NEUTRAL_WEIGHTS.forming);
  push('tension', NEUTRAL_WEIGHTS.tension);
  push('rupture', tail.rupture);
  if ('transform' in tail) {
    push('transform', tail.transform);
  }
  // The last step absorbs the rounding, so the sum is EXACTLY the total.
  steps.push({ phase: 'reveal', durationMs: Math.max(1, total - spent) });
  return steps;
}

/** What one reveal costs in wall-clock time. */
export function revealTotalMs(rarity: Rarity, options: TimelineOptions = {}): number {
  return revealTimeline(rarity, options).reduce((sum, step) => sum + step.durationMs, 0);
}

/**
 * How long the finished prize stays on screen before the next roll starts.
 *
 * Separate from the animation on purpose. {@link REVEAL_DURATION_MS} is how
 * long the SHOW takes; this is how long the player gets to read what they
 * actually got - the name, the rarity, the level, the stars, the pity seal -
 * before it is taken away. Tying the two together meant the pause was whatever
 * the last phase happened to be worth, which was under a second on a Common.
 *
 * It climbs in bands rather than per rarity, because the thing being read is
 * the same four lines either way; what changes is how much a player wants to
 * sit with it.
 */
export const REVEAL_HOLD_MS: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 2000,
  uncommon: 2000,
  rare: 2000,
  epic: 3000,
  mythic: 3000,
  legendary: 4000,
  excellent: 4000,
  perfect: 4000,
  anomaly: 5000,
  prismatic: 5000,
});

/**
 * The pause after one reveal.
 *
 * NOT shortened by `prefers-reduced-motion`: that setting is about movement,
 * and a player who asked for less of it did not ask to be given less time to
 * read. Everything else in this file shrinks; this deliberately does not.
 */
export function revealHoldMs(rarity: Rarity): number {
  return REVEAL_HOLD_MS[rarity];
}

/** What one roll costs end to end: the animation, then the reading pause. */
export function revealCycleMs(rarity: Rarity, options: TimelineOptions = {}): number {
  return revealTotalMs(rarity, options) + revealHoldMs(rarity);
}

/**
 * The instant the rarity stops being a secret, in milliseconds.
 *
 * The start of the rupture, which is {@link RARITY_HIDDEN_SHARE} of the way
 * through for every rarity. Exposed so a test can assert the fraction rather
 * than trusting a comment.
 */
export function colourStartsAt(rarity: Rarity, options: TimelineOptions = {}): number {
  let elapsed = 0;
  for (const step of revealTimeline(rarity, options)) {
    if (step.phase === 'rupture') {
      return elapsed;
    }
    elapsed += step.durationMs;
  }
  return elapsed;
}

/**
 * How many shards the containment vessel is built from.
 *
 * FLAT while the secret holds - the vessel is what replaced the silhouette, and
 * a vessel that grew with the rarity would be the same leak in a new shape. It
 * only gains shards once the portal has already broken.
 */
export const NEUTRAL_SHARDS = 6;
export const MAX_SHARDS = 12;

export function shardCount(rarity: Rarity, intense = false): number {
  if (!intense) {
    return NEUTRAL_SHARDS;
  }
  return Math.min(MAX_SHARDS, NEUTRAL_SHARDS + Math.ceil(revealIntensity(rarity) / 2));
}

/**
 * How many particles a reveal draws, bounded.
 *
 * `intense` is false during the neutral half: a Prismatic that arrived with
 * three times the sparks would announce itself before the rupture just as
 * loudly as a colour would.
 */
export const MAX_REVEAL_PARTICLES = 64;
/** What every rarity draws while the secret holds. Identical for all ten. */
export const NEUTRAL_PARTICLES = 16;

export function particleCount(
  rarity: Rarity,
  options: { intense?: boolean; reducedMotion?: boolean } = {},
): number {
  if (options.reducedMotion) {
    // Enough to keep the shape, few enough to be nearly still.
    return 6;
  }
  if (!options.intense) {
    return NEUTRAL_PARTICLES;
  }
  return Math.min(MAX_REVEAL_PARTICLES, NEUTRAL_PARTICLES + revealIntensity(rarity) * 4);
}

/**
 * How many rings the portal has styles for.
 *
 * Four, and the count below is CLAMPED to it: asking for a fifth would render
 * an unstyled element, which is the kind of mismatch nobody notices until a
 * Prismatic pull looks subtly wrong in production.
 */
export const MAX_REVEAL_RINGS = 4;
/** Rings during the neutral half. The same for every rarity, again on purpose. */
export const NEUTRAL_RINGS = 3;

export function ringCount(rarity: Rarity, intense = true): number {
  if (!intense) {
    return NEUTRAL_RINGS;
  }
  return Math.min(MAX_REVEAL_RINGS, 2 + Math.floor(revealIntensity(rarity) / 3));
}

/**
 * The seal printed under a transformed reveal, or null.
 *
 * Only the rarities that earn a transformation get one, and the word is the
 * rarity's own English name - which is what the rest of the game calls them.
 */
export function revealSeal(rarity: Rarity): string | null {
  return hasTransformation(rarity) ? rarity.toUpperCase() : null;
}

/**
 * Whether this rarity shakes the screen at all.
 *
 * Derived from `SHAKE_MS` rather than restating the two names, so a rarity can
 * never be listed here and be given no duration - or the reverse. Only the two
 * that are meant to feel unstable, and only after the rupture: a shake on every
 * pull is an accessibility problem, not a flourish.
 */
export function allowsShake(rarity: Rarity): boolean {
  return (SHAKE_MS[rarity] ?? 0) > 0;
}

/**
 * The best result in a pull: the one the summary should single out.
 *
 * Ties keep the FIRST, so the highlight is stable rather than depending on how
 * the array happened to be ordered.
 */
export function bestResultIndex(rarities: readonly Rarity[]): number {
  let best = -1;
  let bestRank = -1;
  for (const [index, rarity] of rarities.entries()) {
    const rank = revealIntensity(rarity);
    if (rank > bestRank) {
      bestRank = rank;
      best = index;
    }
  }
  return best;
}

/**
 * How many particles drift across the WHOLE SCREEN once the colour has landed.
 *
 * A second, separate field from the portal's own sparks. It exists only after
 * the rupture, so it can scale with the rarity freely - there is no secret left
 * to keep by the time it appears.
 *
 * Bounded harder than it looks like it needs to be: these cover the entire
 * viewport rather than a 360px box, so each one is a compositor layer over a
 * much larger area, and a hundred of them is a dropped frame on a laptop that
 * is also running the arena behind them.
 */
export const MAX_AMBIENT_MOTES = 56;

export function ambientMoteCount(rarity: Rarity, reducedMotion = false): number {
  if (reducedMotion) {
    // Still coloured, effectively still.
    return 10;
  }
  return Math.min(MAX_AMBIENT_MOTES, 18 + revealIntensity(rarity) * 4);
}

/* -------------------------------------------------------------------------- */
/*  The panel, and the shake                                                  */
/* -------------------------------------------------------------------------- */

/**
 * How many sparks fill the PANEL once the colour has landed.
 *
 * A third field, on top of the portal's own and the full-screen one. The panel
 * is the frame the player is actually looking at, so this is the densest of the
 * three - and like the other two it exists only after the rupture, which is why
 * it can scale freely with the rarity.
 */
export const MAX_PANEL_MOTES = 72;

export function panelMoteCount(rarity: Rarity, reducedMotion = false): number {
  if (reducedMotion) {
    return 12;
  }
  return Math.min(MAX_PANEL_MOTES, 24 + revealIntensity(rarity) * 5);
}

/**
 * How long the screen shakes, and how hard.
 *
 * Only the two rarities whose identity is instability. It starts at the RUPTURE
 * - the end of the suspense - and runs for seconds rather than a few frames,
 * because a 200ms twitch reads as a rendering glitch and a sustained tremor
 * reads as the thing on screen being too much for the screen.
 *
 * Zero for everything else, and zero under reduced motion: a shaking viewport
 * is the single most likely effect in this whole sequence to make somebody feel
 * ill, and it is the first thing that should go.
 */
export const SHAKE_MS: Readonly<Partial<Record<Rarity, number>>> = Object.freeze({
  anomaly: 4000,
  prismatic: 5200,
});

/** Peak displacement in pixels. Prismatic is meant to be worse. */
export const SHAKE_AMPLITUDE_PX: Readonly<Partial<Record<Rarity, number>>> = Object.freeze({
  anomaly: 5,
  prismatic: 9,
});

export function shakeMs(rarity: Rarity, reducedMotion = false): number {
  return reducedMotion ? 0 : (SHAKE_MS[rarity] ?? 0);
}

export function shakeAmplitudePx(rarity: Rarity): number {
  return SHAKE_AMPLITUDE_PX[rarity] ?? 0;
}
